/**
 * DemoPage - Standalone page for testing planner wizard flow
 *
 * Opens in a new tab, full-screen experience for testing the wizard config.
 * Loads config from database and allows walking through questions.
 * Persists answers to database using ModelPlannerService (demo project ID: 15705).
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { RotateCcw, ChevronLeft, ChevronRight, ChevronDown, Check, GitBranch, Cloud, CloudOff } from 'lucide-react';
import PlannerConfigService, { NAV_TOKENS } from '../../../services/plannerConfigService';
import ModelPlannerService from '../../../services/modelPlannerService';

// Demo project ID - hidden project for testing wizard flow
const DEMO_PROJECT_ID = 15705;

/**
 * Build a navigation tree from questions
 * Returns tree nodes with children based on navigation flow
 *
 * @param section - The section config
 * @param answers - Map of answers given (to filter tree to only show taken paths)
 * @param sectionKey - The section key for looking up answers
 * @param completedQuestions - Set of completed question keys (sectionKey.questionId)
 */
const buildNavigationTree = (section, answers = {}, sectionKey = '', completedQuestions = new Set()) => {
  if (!section?.questions || !section?.initialQuestion) return [];

  const questions = section.questions;
  const questionOrder = section.questionOrder || Object.keys(questions);
  const visited = new Set();

  // Helper to get the answer for a question
  const getAnswer = (questionId) => {
    const question = questions[questionId];
    if (!question) return undefined;
    const field = question.field || questionId;
    const answerKey = `${sectionKey}.${questionId}.${field}`;
    return answers[answerKey];
  };

  // Helper to check if a question has been completed
  const isCompleted = (questionId) => {
    return completedQuestions.has(`${sectionKey}.${questionId}`);
  };

  // Helper to resolve LOOP_START tokens to the actual next question
  const resolveLoopStart = (token) => {
    if (token === 'LOOP_START_SINGLE' || token === 'ADA_LOOP_START_SINGLE') {
      const initialQ = section.initialQuestion;
      const nextQuestion = questionOrder.find(qId =>
        qId !== initialQ && !qId.startsWith('are_there_') && !qId.startsWith('has_') && questions[qId]
      ) || questionOrder.find(qId => qId !== initialQ && questions[qId]);
      return nextQuestion || null;
    }
    return null;
  };

  const buildNode = (questionId, branchLabel = null) => {
    // Handle NAV_TOKENS - try to resolve loop starts
    if (NAV_TOKENS.includes(questionId)) {
      const resolved = resolveLoopStart(questionId);
      if (resolved) {
        return buildNode(resolved, branchLabel);
      }
      return null; // SECTION_COMPLETE, LOOP_END, etc.
    }

    if (!questionId || visited.has(questionId)) {
      return null;
    }

    const question = questions[questionId];
    if (!question) return null;

    visited.add(questionId);

    const node = {
      id: questionId,
      question,
      branchLabel,
      children: []
    };

    // Only show children if this question has been completed
    if (!isCompleted(questionId)) {
      // Question not completed yet - tree stops here
      return node;
    }

    // For boolean questions, show the branch that was taken
    if (question.type === 'boolean' || question.type === 'manager_decision') {
      const answer = getAnswer(questionId);

      if (answer === true) {
        // Answered Yes - show Yes branch with label
        const yesChild = buildNode(question.onYes, 'Yes');
        if (yesChild) node.children.push(yesChild);
      } else if (answer === false) {
        // Answered No - show No branch with label
        const noChild = buildNode(question.onNo, 'No');
        if (noChild) node.children.push(noChild);
      }
    } else if (question.next) {
      // Linear navigation - show next question
      const nextChild = buildNode(question.next);
      if (nextChild) node.children.push(nextChild);
    }

    return node;
  };

  const root = buildNode(section.initialQuestion);
  return root ? [root] : [];
};

/**
 * Find the path to a question in the tree
 */
const findPathToQuestion = (nodes, targetId, currentPath = []) => {
  for (const node of nodes) {
    const newPath = [...currentPath, node.id];
    if (node.id === targetId) {
      return newPath;
    }
    if (node.children?.length > 0) {
      const found = findPathToQuestion(node.children, targetId, newPath);
      if (found) return found;
    }
  }
  return null;
};

/**
 * TreeNode - Recursive component to render a navigation tree node
 */
const TreeNode = ({
  node,
  depth = 0,
  currentQuestionId,
  expandedNodes,
  onToggleExpand,
  onNavigate,
  completedQuestions,
  sectionKey,
  pathToCurrentQuestion
}) => {
  const isCurrentQuestion = node.id === currentQuestionId;
  const questionKey = `${sectionKey}.${node.id}`;
  const isCompleted = completedQuestions.has(questionKey);
  const hasChildren = node.children?.length > 0;
  const isExpanded = expandedNodes.has(node.id);
  const isOnPath = pathToCurrentQuestion?.includes(node.id);

  // Determine if this is a branching node (boolean with both Yes and No paths)
  const isBranching = node.question?.type === 'boolean' || node.question?.type === 'manager_decision';

  return (
    <div style={{ marginLeft: depth > 0 ? 12 : 0 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          position: 'relative'
        }}
      >
        {/* Vertical line connector */}
        {depth > 0 && (
          <div style={{
            position: 'absolute',
            left: -8,
            top: 0,
            bottom: '50%',
            width: 1,
            background: 'var(--gray-700)'
          }} />
        )}

        {/* Branch label (Yes/No) */}
        {node.branchLabel && (
          <span style={{
            fontSize: '9px',
            padding: '1px 4px',
            borderRadius: 3,
            background: node.branchLabel === 'Yes' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
            color: node.branchLabel === 'Yes' ? '#22c55e' : '#ef4444',
            marginRight: 2
          }}>
            {node.branchLabel}
          </span>
        )}

        {/* Expand/collapse toggle */}
        {hasChildren ? (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleExpand(node.id); }}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: 'var(--gray-500)',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ChevronDown
              size={12}
              style={{
                transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.15s'
              }}
            />
          </button>
        ) : (
          <span style={{ width: 12 }} />
        )}

        {/* Question button */}
        <button
          onClick={() => onNavigate(node.id)}
          style={{
            flex: 1,
            padding: '3px 6px',
            background: isCurrentQuestion ? 'var(--gray-700)' : 'transparent',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            textAlign: 'left',
            cursor: 'pointer',
            fontSize: '11px',
            color: isCompleted ? '#22c55e' : isCurrentQuestion ? 'var(--gray-100)' : isOnPath ? 'var(--gray-300)' : 'var(--gray-500)',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            overflow: 'hidden'
          }}
        >
          {isCompleted && <Check size={10} style={{ flexShrink: 0 }} />}
          {isBranching && !isCompleted && <GitBranch size={10} style={{ flexShrink: 0, opacity: 0.5 }} />}
          <span style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {node.id}
          </span>
        </button>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div style={{
          borderLeft: '1px solid var(--gray-700)',
          marginLeft: 6,
          paddingLeft: 4,
          marginTop: 2
        }}>
          {node.children.map((child, i) => (
            <TreeNode
              key={`${child.id}-${i}`}
              node={child}
              depth={depth + 1}
              currentQuestionId={currentQuestionId}
              expandedNodes={expandedNodes}
              onToggleExpand={onToggleExpand}
              onNavigate={onNavigate}
              completedQuestions={completedQuestions}
              sectionKey={sectionKey}
              pathToCurrentQuestion={pathToCurrentQuestion}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const DemoPage = () => {
  const [config, setConfig] = useState(null);
  const [session, setSession] = useState(null);
  const [savedAnswers, setSavedAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load config and session on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load planner config
        PlannerConfigService.invalidateCache();
        const configData = await PlannerConfigService.loadConfiguration();
        const { _versionId, _fromDatabase, ...cleanConfig } = configData;
        setConfig(cleanConfig);

        // Load or create demo session
        const { data: sessionData, error: sessionError } = await ModelPlannerService.getOrCreateSession(DEMO_PROJECT_ID);
        if (sessionError) {
          console.error('Failed to get/create session:', sessionError);
        } else {
          setSession(sessionData);

          // Load existing section data
          if (sessionData?.id) {
            const { data: allSectionData } = await ModelPlannerService.getAllSessionSectionData(sessionData.id);
            if (allSectionData?.length > 0) {
              // Merge all answers from all section data records
              const mergedAnswers = {};
              allSectionData.forEach(sectionRecord => {
                if (sectionRecord.answers) {
                  Object.assign(mergedAnswers, sectionRecord.answers);
                }
              });
              setSavedAnswers(mergedAnswers);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--dark-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--gray-400)'
      }}>
        Loading wizard configuration...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--dark-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--red-400)'
      }}>
        Error: {error}
      </div>
    );
  }

  return <DemoWizard config={config} session={session} savedAnswers={savedAnswers} />;
};

/**
 * DemoWizard - The actual wizard demo interface
 */
const DemoWizard = ({ config, session, savedAnswers }) => {
  const sectionKeys = useMemo(() => Object.keys(config?.sections || {}), [config]);

  // Demo state
  const [currentSectionKey, setCurrentSectionKey] = useState(sectionKeys[0] || '');
  const [currentQuestionId, setCurrentQuestionId] = useState(
    config?.sections?.[sectionKeys[0]]?.initialQuestion || ''
  );
  const [answers, setAnswers] = useState(savedAnswers || {});
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [completedQuestions, setCompletedQuestions] = useState(new Set()); // Track completed questions
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState(new Set([sectionKeys[0]])); // Which sections are expanded
  const [expandedNodes, setExpandedNodes] = useState(new Set()); // Which tree nodes are expanded
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const saveTimeoutRef = useRef(null);

  // Initialize completed questions from saved answers
  useEffect(() => {
    if (Object.keys(savedAnswers || {}).length > 0) {
      // Infer completed questions from saved answers
      const completed = new Set();
      Object.keys(savedAnswers).forEach(key => {
        // Key format: sectionKey.questionId.field
        const parts = key.split('.');
        if (parts.length >= 2) {
          completed.add(`${parts[0]}.${parts[1]}`);
        }
      });
      setCompletedQuestions(completed);
    }
  }, [savedAnswers]);

  // Debounced save to database
  const saveAnswersToDatabase = useCallback(async (newAnswers) => {
    if (!session?.id) return;

    // Clear previous timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    setSaveStatus('saving');

    // Debounce save by 500ms
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        // Group answers by section
        const answersBySection = {};
        Object.entries(newAnswers).forEach(([key, value]) => {
          const parts = key.split('.');
          const sectionKey = parts[0];
          if (!answersBySection[sectionKey]) {
            answersBySection[sectionKey] = {};
          }
          answersBySection[sectionKey][key] = value;
        });

        // Save each section's answers
        for (const [sectionKey, sectionAnswers] of Object.entries(answersBySection)) {
          await ModelPlannerService.upsertSingleSectionData(session.id, sectionKey, sectionAnswers);
        }

        setSaveStatus('saved');
        // Reset status after 2 seconds
        setTimeout(() => setSaveStatus('idle'), 2000);
      } catch (error) {
        console.error('Error saving answers:', error);
        setSaveStatus('error');
      }
    }, 500);
  }, [session?.id]);

  // Current section and question
  const currentSection = config?.sections?.[currentSectionKey];
  const currentQuestion = currentSection?.questions?.[currentQuestionId];

  // Build navigation trees for all sections (memoized)
  // Trees are rebuilt when answers/completions change to show only completed path
  const sectionTrees = useMemo(() => {
    const trees = {};
    sectionKeys.forEach(key => {
      trees[key] = buildNavigationTree(config?.sections?.[key], answers, key, completedQuestions);
    });
    return trees;
  }, [config, sectionKeys, answers, completedQuestions]);

  // Find path to current question and auto-expand nodes on that path
  const pathToCurrentQuestion = useMemo(() => {
    const tree = sectionTrees[currentSectionKey];
    if (!tree?.length) return [];
    return findPathToQuestion(tree, currentQuestionId) || [];
  }, [sectionTrees, currentSectionKey, currentQuestionId]);

  // Auto-expand path to current question when question changes
  // Use currentQuestionId as dependency (stable string) not pathToCurrentQuestion (new array each render)
  useEffect(() => {
    const tree = sectionTrees[currentSectionKey];
    if (!tree?.length) return;
    const path = findPathToQuestion(tree, currentQuestionId) || [];
    if (path.length > 0) {
      setExpandedNodes(prev => {
        // Only update if there are new nodes to add
        const hasNew = path.some(id => !prev.has(id));
        if (!hasNew) return prev; // Return same reference to avoid re-render
        const next = new Set(prev);
        path.forEach(id => next.add(id));
        return next;
      });
      // Also expand the current section
      setExpandedSections(prev => {
        if (prev.has(currentSectionKey)) return prev; // Already expanded
        const next = new Set(prev);
        next.add(currentSectionKey);
        return next;
      });
    }
  }, [currentQuestionId, currentSectionKey, sectionTrees]);

  // Toggle section expand/collapse
  const toggleSection = useCallback((sectionKey) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionKey)) {
        next.delete(sectionKey);
      } else {
        next.add(sectionKey);
      }
      return next;
    });
  }, []);

  // Toggle tree node expand/collapse
  const toggleNode = useCallback((nodeId) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Reset demo
  const handleReset = useCallback(async () => {
    setCurrentSectionKey(sectionKeys[0] || '');
    setCurrentQuestionId(config?.sections?.[sectionKeys[0]]?.initialQuestion || '');
    setAnswers({});
    setNavigationHistory([]);
    setCompletedQuestions(new Set());

    // Clear all planner data for the demo project
    try {
      await ModelPlannerService.clearPlannerData(DEMO_PROJECT_ID);
    } catch (error) {
      console.error('Error clearing planner data:', error);
    }
  }, [sectionKeys, config]);

  // Navigate to question
  const navigateTo = useCallback((sectionKey, questionId) => {
    setNavigationHistory(prev => [...prev, { sectionKey: currentSectionKey, questionId: currentQuestionId }]);
    setCurrentSectionKey(sectionKey);
    setCurrentQuestionId(questionId);
  }, [currentSectionKey, currentQuestionId]);

  // Go back
  const goBack = useCallback(() => {
    if (navigationHistory.length === 0) return;
    const prev = navigationHistory[navigationHistory.length - 1];
    setNavigationHistory(h => h.slice(0, -1));
    setCurrentSectionKey(prev.sectionKey);
    setCurrentQuestionId(prev.questionId);
  }, [navigationHistory]);

  // Save a field value without navigation (for form fields during typing)
  const saveFieldValue = useCallback((field, value) => {
    const answerKey = `${currentSectionKey}.${currentQuestionId}.${field}`;
    const newAnswers = { ...answers, [answerKey]: value };
    setAnswers(newAnswers);
    saveAnswersToDatabase(newAnswers);
  }, [currentSectionKey, currentQuestionId, answers, saveAnswersToDatabase]);

  // Handle answer (with navigation)
  const handleAnswer = useCallback((field, value) => {
    let newAnswers = answers;
    if (field !== '_navigate') {
      const answerKey = `${currentSectionKey}.${currentQuestionId}.${field}`;
      newAnswers = { ...answers, [answerKey]: value };
      setAnswers(newAnswers);
      // Save to database
      saveAnswersToDatabase(newAnswers);
    }

    // Mark current question as completed
    const questionKey = `${currentSectionKey}.${currentQuestionId}`;
    setCompletedQuestions(prev => new Set([...prev, questionKey]));

    const question = currentSection?.questions?.[currentQuestionId];
    if (!question) return;

    let nextId = null;

    if (field === '_navigate' && value) {
      nextId = value;
    } else if (question.type === 'boolean' || question.type === 'manager_decision') {
      nextId = value === true || value === 'yes' ? question.onYes : question.onNo;
    } else {
      nextId = question.next;
    }

    if (!nextId) return;

    if (NAV_TOKENS.includes(nextId)) {
      if (nextId === 'SECTION_COMPLETE' || nextId === 'LOOP_END') {
        const currentIdx = sectionKeys.indexOf(currentSectionKey);
        if (currentIdx < sectionKeys.length - 1) {
          const nextSectionKey = sectionKeys[currentIdx + 1];
          const nextSection = config?.sections?.[nextSectionKey];
          navigateTo(nextSectionKey, nextSection?.initialQuestion || '');
        }
        return;
      }

      if (nextId === 'LOOP_START_SINGLE' || nextId === 'LOOP_START' || nextId === 'ADA_LOOP_START_SINGLE') {
        const questions = currentSection?.questions || {};
        const questionIds = currentSection?.questionOrder || Object.keys(questions);
        const initialQ = currentSection?.initialQuestion;
        const nextQuestion = questionIds.find(qId =>
          qId !== initialQ && !qId.startsWith('are_there_') && questions[qId]
        );
        if (nextQuestion) {
          navigateTo(currentSectionKey, nextQuestion);
        }
        return;
      }

      if (nextId === 'LOOP_CONTINUE' || nextId === 'ADA_LOOP_CONTINUE') {
        const currentIdx = sectionKeys.indexOf(currentSectionKey);
        if (currentIdx < sectionKeys.length - 1) {
          const nextSectionKey = sectionKeys[currentIdx + 1];
          const nextSection = config?.sections?.[nextSectionKey];
          navigateTo(nextSectionKey, nextSection?.initialQuestion || '');
        }
        return;
      }
      return;
    }

    if (currentSection?.questions?.[nextId]) {
      navigateTo(currentSectionKey, nextId);
    }
  }, [currentSectionKey, currentQuestionId, currentSection, sectionKeys, config, navigateTo, answers, saveAnswersToDatabase]);

  // Get current answer
  const getCurrentAnswer = useCallback((field) => {
    const answerKey = `${currentSectionKey}.${currentQuestionId}.${field}`;
    return answers[answerKey];
  }, [currentSectionKey, currentQuestionId, answers]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--dark-bg)',
      display: 'flex'
    }}>
      {/* Sidebar */}
      <div style={{
        width: sidebarCollapsed ? 50 : 280,
        background: 'var(--dark-surface)',
        borderRight: '1px solid var(--gray-700)',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s'
      }}>
        {/* Sidebar Header */}
        <div style={{
          padding: 'var(--space-3)',
          borderBottom: '1px solid var(--gray-700)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          {!sidebarCollapsed && (
            <span style={{ color: 'var(--gray-300)', fontWeight: 500, fontSize: 'var(--text-sm)' }}>
              Sections
            </span>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--gray-400)',
              cursor: 'pointer',
              padding: 4
            }}
          >
            {sidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        {/* Section List with Navigation Trees */}
        {!sidebarCollapsed && (
          <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-2)' }}>
            {sectionKeys.map((sectionKey) => {
              const section = config?.sections?.[sectionKey];
              const isActiveSection = sectionKey === currentSectionKey;
              const tree = sectionTrees[sectionKey] || [];
              const isExpanded = expandedSections.has(sectionKey);
              const questionCount = Object.keys(section?.questions || {}).length;

              return (
                <div key={sectionKey} style={{ marginBottom: 'var(--space-2)' }}>
                  {/* Section Header */}
                  <button
                    onClick={() => toggleSection(sectionKey)}
                    style={{
                      width: '100%',
                      padding: 'var(--space-2)',
                      background: isActiveSection ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                      border: 'none',
                      borderRadius: 'var(--radius-md)',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-1)',
                      color: isActiveSection ? 'var(--blue-300)' : 'var(--gray-400)'
                    }}
                  >
                    <ChevronDown
                      size={14}
                      style={{
                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 0.15s',
                        flexShrink: 0
                      }}
                    />
                    <span style={{
                      fontWeight: isActiveSection ? 500 : 400,
                      fontSize: 'var(--text-sm)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {section?.label || sectionKey}
                    </span>
                    <span style={{
                      fontSize: '10px',
                      color: 'var(--gray-500)',
                      marginLeft: 'auto',
                      flexShrink: 0
                    }}>
                      {questionCount}
                    </span>
                  </button>

                  {/* Navigation Tree (when expanded) */}
                  {isExpanded && tree.length > 0 && (
                    <div style={{ paddingLeft: 'var(--space-2)', marginTop: 'var(--space-1)' }}>
                      {tree.map((node, i) => (
                        <TreeNode
                          key={`${node.id}-${i}`}
                          node={node}
                          currentQuestionId={isActiveSection ? currentQuestionId : null}
                          expandedNodes={expandedNodes}
                          onToggleExpand={toggleNode}
                          onNavigate={(qId) => navigateTo(sectionKey, qId)}
                          completedQuestions={completedQuestions}
                          sectionKey={sectionKey}
                          pathToCurrentQuestion={isActiveSection ? pathToCurrentQuestion : []}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{
          padding: 'var(--space-3) var(--space-4)',
          borderBottom: '1px solid var(--gray-700)',
          background: 'var(--dark-surface)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ color: 'var(--gray-300)', display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <div>
              <span style={{ color: 'var(--gray-500)' }}>{currentSection?.label || currentSectionKey}</span>
              <span style={{ color: 'var(--gray-600)', margin: '0 8px' }}>/</span>
              <span>{currentQuestionId}</span>
            </div>
            {/* Save status indicator */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 'var(--text-xs)',
              color: saveStatus === 'saving' ? 'var(--amber-400)' :
                     saveStatus === 'saved' ? 'var(--green-400)' :
                     saveStatus === 'error' ? 'var(--red-400)' : 'var(--gray-500)'
            }}>
              {saveStatus === 'saving' && <><Cloud size={12} /> Saving...</>}
              {saveStatus === 'saved' && <><Cloud size={12} /> Saved</>}
              {saveStatus === 'error' && <><CloudOff size={12} /> Save failed</>}
              {saveStatus === 'idle' && session?.id && <><Cloud size={12} style={{ opacity: 0.5 }} /> Project {DEMO_PROJECT_ID}</>}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              onClick={handleReset}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                padding: 'var(--space-2) var(--space-3)',
                background: 'var(--gray-700)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                color: 'var(--gray-300)',
                cursor: 'pointer'
              }}
            >
              <RotateCcw size={14} />
              Reset
            </button>
            <button
              onClick={() => window.close()}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-1)',
                padding: 'var(--space-2) var(--space-3)',
                background: 'var(--gray-700)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                color: 'var(--gray-300)',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>

        {/* Question Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: 'var(--space-8)',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <div style={{ maxWidth: 700, width: '100%' }}>
            {currentQuestion ? (
              <QuestionCard
                question={currentQuestion}
                questionId={currentQuestionId}
                onAnswer={handleAnswer}
                onSaveField={saveFieldValue}
                getAnswer={getCurrentAnswer}
                onBack={goBack}
                canGoBack={navigationHistory.length > 0}
              />
            ) : (
              <div style={{
                textAlign: 'center',
                color: 'var(--gray-400)',
                padding: 'var(--space-8)'
              }}>
                Question "{currentQuestionId}" not found
              </div>
            )}
          </div>
        </div>

        {/* Footer with progress */}
        <div style={{
          padding: 'var(--space-3) var(--space-4)',
          borderTop: '1px solid var(--gray-700)',
          background: 'var(--dark-surface)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: 'var(--text-sm)',
          color: 'var(--gray-500)'
        }}>
          <div>
            Step {navigationHistory.length + 1} • {Object.keys(answers).length} answers recorded
          </div>
          <div>
            Model Planner Config Demo
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * QuestionCard - Renders a question with input controls
 */
const QuestionCard = ({ question, questionId, onAnswer, onSaveField, getAnswer, onBack, canGoBack }) => {
  const isBoolean = question.type === 'boolean' || question.type === 'manager_decision';

  // Track form field values for grouped_form validation
  const [formValues, setFormValues] = useState({});
  const [validationError, setValidationError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null); // For image lightbox

  // Initialize form values from saved answers when question changes
  useEffect(() => {
    setValidationError(null);

    // For grouped_form, load saved values for each field
    if (question.type === 'grouped_form' && question.fields) {
      const savedValues = {};
      question.fields.forEach(field => {
        const savedValue = getAnswer(field.field);
        if (savedValue !== undefined) {
          savedValues[field.field] = savedValue;
        }
      });
      setFormValues(savedValues);
    } else {
      setFormValues({});
    }
  }, [questionId, question.type, question.fields, getAnswer]);

  // Handle form field change - update local state AND persist to database (without navigation)
  const handleFormFieldChange = (fieldName, value) => {
    setFormValues(prev => ({ ...prev, [fieldName]: value }));
    setValidationError(null); // Clear error when user types
    // Persist to database immediately (without triggering navigation)
    onSaveField(fieldName, value);
  };

  // Validate grouped_form fields
  const validateGroupedForm = () => {
    if (question.type !== 'grouped_form' || !question.fields) return true;

    // All fields are required unless explicitly marked required: false
    const requiredFields = question.fields.filter(f => f.required !== false);
    for (const field of requiredFields) {
      const value = formValues[field.field];
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        const fieldLabel = field.text || field.field;
        if (field.type === 'image') {
          setValidationError(`Please paste a screenshot for: ${fieldLabel}`);
        } else {
          setValidationError(`Please fill in: ${fieldLabel}`);
        }
        return false;
      }
    }
    return true;
  };

  // Handle Continue click with validation
  const handleContinueClick = () => {
    if (validateGroupedForm()) {
      onAnswer('_navigate', question.next);
    }
  };

  // Back button component to reuse
  const BackButton = () => canGoBack ? (
    <button
      onClick={onBack}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-1)',
        padding: 'var(--space-3) var(--space-4)',
        background: 'var(--gray-700)',
        border: 'none',
        borderRadius: 'var(--radius-md)',
        color: 'var(--gray-300)',
        cursor: 'pointer'
      }}
    >
      <ChevronLeft size={16} />
      Back
    </button>
  ) : null;

  return (
    <div style={{
      background: 'var(--dark-surface)',
      border: '1px solid var(--gray-700)',
      borderRadius: 'var(--radius-lg)',
      padding: 'var(--space-6)'
    }}>
      {/* Question header */}
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-2)'
        }}>
          <span style={{
            fontSize: '10px',
            color: 'var(--gray-400)',
            background: 'var(--gray-700)',
            padding: '2px 8px',
            borderRadius: 'var(--radius-sm)'
          }}>
            {question.type}
          </span>
          <code style={{ fontSize: '11px', color: 'var(--gray-500)' }}>
            {questionId}
          </code>
        </div>
        <h2 style={{
          margin: 0,
          fontSize: 'var(--text-xl)',
          fontWeight: 500,
          color: 'var(--gray-100)'
        }}>
          {question.text || questionId}
        </h2>
      </div>

      {/* Answer input */}
      <div style={{ marginTop: 'var(--space-4)' }}>
        {isBoolean ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
              {canGoBack && <BackButton />}
              <button
                onClick={() => onAnswer(question.field || questionId, true)}
                style={{
                  flex: 1,
                  padding: 'var(--space-4)',
                  background: getAnswer(question.field || questionId) === true ? '#15803d' : '#374151',
                  border: getAnswer(question.field || questionId) === true ? '2px solid #22c55e' : '2px solid transparent',
                  borderRadius: 'var(--radius-md)',
                  color: 'white',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Yes
              </button>
              <button
                onClick={() => onAnswer(question.field || questionId, false)}
                style={{
                  flex: 1,
                  padding: 'var(--space-4)',
                  background: getAnswer(question.field || questionId) === false ? '#991b1b' : '#374151',
                  border: getAnswer(question.field || questionId) === false ? '2px solid #ef4444' : '2px solid transparent',
                  borderRadius: 'var(--radius-md)',
                  color: 'white',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                No
              </button>
            </div>
          </div>
        ) : question.type === 'text' || question.type === 'elevation' ? (
          <div>
            <input
              type={question.type === 'elevation' ? 'number' : 'text'}
              placeholder={question.placeholder || 'Enter value...'}
              style={{
                width: '100%',
                padding: 'var(--space-3)',
                background: 'var(--gray-800)',
                border: '1px solid var(--gray-600)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--gray-100)',
                fontSize: 'var(--text-base)'
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onAnswer(question.field || questionId, e.target.value);
                }
              }}
            />
            <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--text-xs)', color: 'var(--gray-500)' }}>
              Press Enter to continue
            </p>
            {canGoBack && <div style={{ marginTop: 'var(--space-3)' }}><BackButton /></div>}
          </div>
        ) : question.type === 'choice' && question.options ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {question.options.map((opt, i) => (
              <button
                key={i}
                onClick={() => onAnswer(question.field || questionId, opt.value || opt)}
                style={{
                  padding: 'var(--space-3)',
                  background: 'var(--gray-700)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--gray-100)',
                  textAlign: 'left',
                  cursor: 'pointer'
                }}
              >
                {opt.label || opt}
              </button>
            ))}
            {canGoBack && <div style={{ marginTop: 'var(--space-2)' }}><BackButton /></div>}
          </div>
        ) : question.type === 'grouped_form' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {question.fields?.map((field, i) => (
              <div key={i}>
                <label style={{ display: 'block', fontSize: 'var(--text-sm)', color: 'var(--gray-300)', marginBottom: 4 }}>
                  {field.text || field.field}
                  {field.required !== false && (
                    <span style={{ color: 'var(--red-400)', marginLeft: 4 }}>*</span>
                  )}
                </label>
                {field.type === 'image' ? (
                  <div
                    tabIndex={0}
                    onPaste={(e) => {
                      const items = e.clipboardData?.items;
                      if (items) {
                        for (const item of items) {
                          if (item.type.startsWith('image/')) {
                            const file = item.getAsFile();
                            if (file) {
                              // Convert to base64 data URL for display
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                handleFormFieldChange(field.field, event.target.result);
                              };
                              reader.readAsDataURL(file);
                            }
                            break;
                          }
                        }
                      }
                    }}
                    style={{
                      padding: 'var(--space-4)',
                      background: formValues[field.field] ? 'rgba(34, 197, 94, 0.1)' : 'var(--gray-800)',
                      border: formValues[field.field] ? '2px solid #22c55e' : '2px dashed var(--gray-600)',
                      borderRadius: 'var(--radius-md)',
                      textAlign: 'center',
                      color: formValues[field.field] ? '#22c55e' : 'var(--gray-500)',
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                  >
                    {formValues[field.field]?.startsWith('data:image') ? (
                      <div style={{ position: 'relative' }}>
                        <img
                          src={formValues[field.field]}
                          alt="Pasted screenshot"
                          style={{
                            maxWidth: '100%',
                            maxHeight: '200px',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'zoom-in'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setLightboxImage(formValues[field.field]);
                          }}
                        />
                        <div style={{
                          fontSize: 'var(--text-xs)',
                          color: 'var(--gray-400)',
                          marginTop: 'var(--space-1)'
                        }}>
                          Click image to enlarge
                        </div>
                      </div>
                    ) : formValues[field.field] ? (
                      '✓ Screenshot pasted'
                    ) : (
                      'Click here and paste screenshot (Cmd+V)'
                    )}
                  </div>
                ) : field.type === 'elevation' ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <input
                      type="number"
                      placeholder="0.00"
                      value={formValues[field.field] || ''}
                      onChange={(e) => handleFormFieldChange(field.field, e.target.value)}
                      style={{
                        flex: 1,
                        padding: 'var(--space-2)',
                        background: 'var(--gray-800)',
                        border: '1px solid var(--gray-600)',
                        borderRadius: 'var(--radius-sm)',
                        color: 'var(--gray-100)'
                      }}
                    />
                    {field.allowNA && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--gray-400)', fontSize: 'var(--text-sm)' }}>
                        <input
                          type="checkbox"
                          checked={formValues[`${field.field}_na`] || false}
                          onChange={(e) => {
                            handleFormFieldChange(`${field.field}_na`, e.target.checked);
                            if (e.target.checked) {
                              handleFormFieldChange(field.field, 'N/A');
                            }
                          }}
                        /> N/A
                      </label>
                    )}
                  </div>
                ) : (
                  <input
                    type="text"
                    placeholder={field.placeholder || ''}
                    value={formValues[field.field] || ''}
                    onChange={(e) => handleFormFieldChange(field.field, e.target.value)}
                    style={{
                      width: '100%',
                      padding: 'var(--space-2)',
                      background: 'var(--gray-800)',
                      border: '1px solid var(--gray-600)',
                      borderRadius: 'var(--radius-sm)',
                      color: 'var(--gray-100)'
                    }}
                  />
                )}
              </div>
            ))}
            {/* Validation error */}
            {validationError && (
              <div style={{
                padding: 'var(--space-2) var(--space-3)',
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--red-400)',
                fontSize: 'var(--text-sm)'
              }}>
                {validationError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
              {canGoBack && <BackButton />}
              <button
                onClick={handleContinueClick}
                style={{
                  padding: 'var(--space-3) var(--space-4)',
                  background: 'var(--cta-blue)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                Continue
              </button>
            </div>
          </div>
        ) : question.type === 'multi_checkbox' ? (
          <div>
            {(question.options || question.choices || []).map((opt, i) => (
              <label key={i} style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                padding: 'var(--space-2)',
                color: 'var(--gray-300)',
                cursor: 'pointer'
              }}>
                <input type="checkbox" style={{ accentColor: 'var(--cta-blue)' }} />
                {opt.label || opt}
              </label>
            ))}
            <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
              {canGoBack && <BackButton />}
              <button
                onClick={() => onAnswer('_navigate', question.next)}
                style={{
                  padding: 'var(--space-3) var(--space-4)',
                  background: 'var(--cta-blue)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                Continue
              </button>
            </div>
          </div>
        ) : question.type === 'confirmation' ? (
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            {canGoBack && <BackButton />}
            <button
              onClick={() => onAnswer('_navigate', question.next)}
              style={{
                padding: 'var(--space-3) var(--space-6)',
                background: 'var(--green-600)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                color: 'white',
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              Confirm & Continue
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            {canGoBack && <BackButton />}
            <button
              onClick={() => onAnswer('_navigate', question.next)}
              style={{
                padding: 'var(--space-3) var(--space-4)',
                background: 'var(--cta-blue)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                color: 'white',
                cursor: 'pointer'
              }}
            >
              Continue
            </button>
          </div>
        )}
      </div>

      {/* Navigation hints */}
      <div style={{
        marginTop: 'var(--space-4)',
        paddingTop: 'var(--space-3)',
        borderTop: '1px solid var(--gray-700)',
        fontSize: 'var(--text-xs)',
        color: 'var(--gray-500)'
      }}>
        {isBoolean ? (
          <>Yes → <code>{question.onYes}</code> | No → <code>{question.onNo}</code></>
        ) : question.next ? (
          <>Next → <code>{question.next}</code></>
        ) : (
          <>No navigation configured</>
        )}
      </div>

      {/* Image lightbox modal */}
      {lightboxImage && (
        <div
          onClick={() => setLightboxImage(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            cursor: 'zoom-out',
            padding: 'var(--space-4)'
          }}
        >
          <img
            src={lightboxImage}
            alt="Enlarged screenshot"
            style={{
              maxWidth: '95vw',
              maxHeight: '95vh',
              objectFit: 'contain',
              borderRadius: 'var(--radius-md)',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <div style={{
            position: 'absolute',
            top: 'var(--space-4)',
            right: 'var(--space-4)',
            color: 'white',
            fontSize: 'var(--text-sm)',
            opacity: 0.7
          }}>
            Click anywhere to close
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoPage;
